import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, runInAction } from "mobx";
import {
  collection,
  deleteField,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";

import db from "../FirestoreConfig";
import { getCachedCatalogFoods } from "../../nutrition/foods/catalogFoodSource";
import {
  sortNutritionFoods,
  sortNutritionRecipes,
} from "./nutritionRecords";
import {
  buildNutritionEquivalentDocPath,
  buildNutritionEquivalentsCollectionPath,
  buildNutritionFoodDocPath,
  buildNutritionFoodsCollectionPath,
  buildNutritionPlanDocPath,
  buildNutritionPlanMealDocPath,
  buildNutritionPlanMealsCollectionPath,
  buildNutritionPlansCollectionPath,
  buildNutritionRecipeDocPath,
  buildNutritionRecipesCollectionPath,
  buildNutritionRootDocPath,
} from "./nutritionPaths";
import {
  cloneMeal,
  createInitialPlanDraft,
  isPlanDraftSemanticallyEmpty,
  serializePlanDraftForSave,
} from "../../nutrition/plans/nutritionPlanLogic";
import {
  isEquivalentDraftSemanticallyEmpty,
} from "../../nutrition/equivalents/equivalentLogic";
import {
  isRecipeDraftSemanticallyEmpty,
} from "../../nutrition/recipes/recipeLogic";
import {
  COACH_NUTRITION_RESOURCE_EQUIVALENTS,
  COACH_NUTRITION_RESOURCE_FOODS,
  COACH_NUTRITION_RESOURCE_ORDER,
  COACH_NUTRITION_RESOURCE_PLANS,
  COACH_NUTRITION_RESOURCE_RECIPES,
  normalizeCoachNutritionResourceKeys,
} from "../../nutrition/coachNutritionBootstrap";
import { buildCoachNutritionPlanLibraryExportDraft } from "../../nutrition/plans/nutritionPlanLibraryExport";
import {
  deletePendingNutritionPlanTransactional,
  movePendingNutritionPlanToLibraryTransactional,
} from "../../nutrition/plans/pendingNutritionPlanActions";
import { resolveNutritionPlanActionSnapshot } from "../../nutrition/plans/nutritionPlanActionSnapshot";
import SyncDomainController, {
  createFatalSyncError,
  SyncErrorKind,
} from "../../sync/SyncDomainController";
import NutritionFood from "./NutritionFood";
import NutritionRecipe from "./NutritionRecipe";
import NutritionEquivalent from "./NutritionEquivalent";
import CoachNutritionPlan, { CoachNutritionPlanMeal } from "./CoachNutritionPlan";
import NutritionPlan from "./NutritionPlan";
import {
  clonePlainValue,
  diffTopLevelPaths,
  mergeServerIntoLocalByTouchedPaths,
} from "./OptimisticNutritionEntity";
import { resolveNutritionDomainStatus } from "./nutritionSyncStatus";

function sortByName(items) {
  return [...(Array.isArray(items) ? items : [])].sort((leftItem, rightItem) =>
    String(leftItem?.name ?? "").localeCompare(String(rightItem?.name ?? ""), undefined, {
      sensitivity: "base",
    }),
  );
}

function normalizeId(value, fallback = uuidv4()) {
  return String(value ?? fallback).trim().toLowerCase();
}

function createEntityKey(entityType, entityId) {
  return `nutrition:${entityType}:${normalizeId(entityId, entityType)}`;
}

function createPlanReadKey(planId, suffix) {
  return `nutrition:plan:${normalizeId(planId, "plan")}:${suffix}`;
}

function isPlanReadKey(readKey) {
  return String(readKey ?? "").startsWith("nutrition:plan:");
}

function noop() {}

function areEqualByJson(leftValue, rightValue) {
  return JSON.stringify(leftValue ?? null) === JSON.stringify(rightValue ?? null);
}

function sortMealsByOrder(meals) {
  return [...(Array.isArray(meals) ? meals : [])].sort(
    (leftMeal, rightMeal) => Number(leftMeal?.order ?? 0) - Number(rightMeal?.order ?? 0),
  );
}

function hasPendingNutritionPlanInSummary(summary, planId) {
  const normalizedPlanId = normalizeId(planId, "plan");
  if (!normalizedPlanId) {
    return false;
  }

  const nutritionPlans = Array.isArray(summary?.nutritionPlans)
    ? summary.nutritionPlans
    : [];

  return nutritionPlans.some(
    (planRecord) => normalizeId(planRecord?.id, "plan") === normalizedPlanId,
  );
}

function collectDocs(snapshot) {
  if (typeof snapshot?.docChanges === "function") {
    return snapshot.docChanges();
  }

  if (Array.isArray(snapshot?.docs)) {
    return snapshot.docs.map((docSnapshot) => ({
      type: "added",
      doc: docSnapshot,
    }));
  }

  return [];
}

function reconcileCollection(currentItems, snapshot, mapDoc, sortItems) {
  const nextItemsById = new Map(
    (Array.isArray(currentItems) ? currentItems : []).map((item) => [
      String(item?.id ?? "").trim().toLowerCase(),
      item,
    ]),
  );

  collectDocs(snapshot).forEach((change) => {
    const docSnapshot = change?.doc;
    if (!docSnapshot?.id) {
      return;
    }

    const normalizedId = String(docSnapshot.id).trim().toLowerCase();
    if (change.type === "removed") {
      nextItemsById.delete(normalizedId);
      return;
    }

    nextItemsById.set(normalizedId, mapDoc(docSnapshot));
  });

  return sortItems(Array.from(nextItemsById.values()));
}

function assertMatchingRevision(snapshot, expectedRev) {
  if (expectedRev == null) {
    return;
  }

  const currentRev = Number(snapshot?.data()?._rev ?? 0);
  if (currentRev !== Number(expectedRev)) {
    const error = new Error("Nutrition document revision mismatch");
    error.syncErrorKind = SyncErrorKind.BLOCKED;
    error.currentRev = currentRev;
    error.expectedRev = Number(expectedRev);
    throw error;
  }
}

function buildPlanFromRootOnly(planRecord = {}) {
  return NutritionPlan.fromRecord(planRecord, [], {
    mealsHydrated: false,
    store: null,
  });
}

function buildPlanDraftForMerge(planRecord = {}, mealRecords = []) {
  const draft = createInitialPlanDraft({
    ...planRecord,
    meals: Array.isArray(mealRecords) ? mealRecords : [],
  });

  if (!Array.isArray(mealRecords) || mealRecords.length === 0) {
    draft.meals = [];
  }

  return draft;
}

function buildPlanMealsQuery(coachId, planId) {
  return query(
    collection(
      db,
      ...buildNutritionPlanMealsCollectionPath(coachId, planId),
    ),
    orderBy("order", "asc"),
  );
}

function applyQueuedDraftToServerRecord(entity) {
  const touchedPaths = Array.isArray(entity?.touchedPaths) ? entity.touchedPaths : [];
  const serverDraft = entity.serverBase != null
    ? clonePlainValue(entity.serverBase)
    : entity.getDraftSnapshot();
  const localDraft = entity.getDraftSnapshot();

  return mergeServerIntoLocalByTouchedPaths(serverDraft, localDraft, touchedPaths);
}

export class CoachNutritionStore {
  foodEntitiesById = new Map();
  recipeEntitiesById = new Map();
  equivalentEntitiesById = new Map();
  planEntitiesById = new Map();
  isFetchingFoods = false;
  isFetchingRecipes = false;
  isFetchingEquivalents = false;
  isFetchingPlans = false;
  hasFetchedFoods = false;
  hasFetchedRecipes = false;
  hasFetchedEquivalents = false;
  hasFetchedPlans = false;
  syncController = null;
  planRootRecordsById = new Map();
  planMealsByPlanId = new Map();

  constructor(coach) {
    this.coach = coach;
    this.syncController = new SyncDomainController("nutrition");
    makeAutoObservable(
      this,
      {
        coach: false,
      },
      { autoBind: true },
    );
  }

  get foodsReadKey() {
    return "nutrition:read:foods";
  }

  get recipesReadKey() {
    return "nutrition:read:recipes";
  }

  get equivalentsReadKey() {
    return "nutrition:read:equivalents";
  }

  get plansReadKey() {
    return "nutrition:read:plans";
  }

  get sortedFoods() {
    return sortNutritionFoods(this.foods);
  }

  get sortedRecipes() {
    return sortNutritionRecipes(this.recipes);
  }

  get sortedEquivalents() {
    return sortByName(this.equivalents);
  }

  get sortedPlans() {
    return sortByName(this.plans);
  }

  get allFoodsForPicker() {
    return [...this.sortedFoods, ...getCachedCatalogFoods()];
  }

  get foods() {
    return Array.from(this.foodEntitiesById.values());
  }

  get recipes() {
    return Array.from(this.recipeEntitiesById.values());
  }

  get equivalents() {
    return Array.from(this.equivalentEntitiesById.values());
  }

  get plans() {
    return Array.from(this.planEntitiesById.values());
  }

  get hasUnsyncedLocalChanges() {
    return this.foods.some((food) => food?.hasUnsyncedChanges) ||
      this.recipes.some((recipe) => recipe?.hasUnsyncedChanges) ||
      this.equivalents.some((equivalent) => equivalent?.hasUnsyncedChanges) ||
      this.plans.some((plan) => plan?.hasUnsyncedChanges);
  }

  get hasLoadedInitialData() {
    return (
      this.hasFetchedFoods &&
      this.hasFetchedRecipes &&
      this.hasFetchedEquivalents &&
      this.hasFetchedPlans
    );
  }

  get syncStatus() {
    return this.status;
  }

  get status() {
    return resolveNutritionDomainStatus({
      controllerStatus: this.syncController?.status,
      entities: [...this.foods, ...this.recipes, ...this.equivalents, ...this.plans],
    });
  }

  retryFailed() {
    this.syncController?.retryFailed?.();
  }

  flush(entityKey = null) {
    this.syncController?.flush?.(entityKey);
  }

  hydrateFoodRecord(foodRecord) {
    return NutritionFood.fromRecord(foodRecord, {
      coach: this.coach,
      store: this,
    });
  }

  hydrateRecipeRecord(recipeRecord) {
    return NutritionRecipe.fromRecord(recipeRecord, { store: this });
  }

  hydrateEquivalentRecord(equivalentRecord) {
    return NutritionEquivalent.fromRecord(equivalentRecord, { store: this });
  }

  hydratePlanRecord(planRecord, mealRecords = [], options = {}) {
    return NutritionPlan.fromRecord(planRecord, mealRecords, {
      store: this,
      mealsHydrated: options.mealsHydrated ?? Array.isArray(mealRecords),
    });
  }

  getEntityMap(entityType) {
    if (entityType === "food") {
      return this.foodEntitiesById;
    }

    if (entityType === "recipe") {
      return this.recipeEntitiesById;
    }

    if (entityType === "equivalent") {
      return this.equivalentEntitiesById;
    }

    if (entityType === "plan") {
      return this.planEntitiesById;
    }

    return null;
  }

  getPersistedEntityIds(entityType) {
    const persistedIds = new Set();

    this.getEntityMap(entityType)?.forEach((entity, entityId) => {
      if (Number(entity?._rev ?? 0) > 0 || entity?.serverBase != null) {
        persistedIds.add(normalizeId(entityId, entityType));
      }
    });

    return persistedIds;
  }

  reconcileInitialEntitySnapshot(entityType, snapshot, mapDoc, upsertEntity) {
    const serverEntityIds = new Set();

    (Array.isArray(snapshot?.docs) ? snapshot.docs : []).forEach((docSnapshot) => {
      if (!docSnapshot?.id) {
        return;
      }

      const normalizedId = normalizeId(docSnapshot.id, entityType);
      serverEntityIds.add(normalizedId);
      upsertEntity(mapDoc(docSnapshot));
    });

    this.getPersistedEntityIds(entityType).forEach((entityId) => {
      if (!serverEntityIds.has(entityId)) {
        this.removeEntity(entityType, entityId);
      }
    });
  }

  getPersistedPlanIds() {
    const persistedIds = new Set();

    this.planRootRecordsById.forEach((planRecord, planId) => {
      if (Number(planRecord?._rev ?? 0) > 0) {
        persistedIds.add(normalizeId(planId, "plan"));
      }
    });

    this.planEntitiesById.forEach((entity, planId) => {
      if (Number(entity?._rev ?? 0) > 0 || entity?.serverBase != null) {
        persistedIds.add(normalizeId(planId, "plan"));
      }
    });

    return persistedIds;
  }

  reconcileInitialPlansSnapshot(plansSnapshot) {
    const serverPlanIds = new Set();

    (Array.isArray(plansSnapshot?.docs) ? plansSnapshot.docs : []).forEach((docSnapshot) => {
      if (!docSnapshot?.id) {
        return;
      }

      const normalizedPlanId = normalizeId(docSnapshot.id, "plan");
      serverPlanIds.add(normalizedPlanId);
      this.updatePlanCache(
        normalizedPlanId,
        {
          id: normalizedPlanId,
          ...docSnapshot.data(),
        },
        null,
      );
    });

    this.getPersistedPlanIds().forEach((planId) => {
      if (!serverPlanIds.has(planId)) {
        this.removePlanFromStore(planId);
      }
    });
  }

  buildMealsMapFromSnapshot(mealsSnapshot) {
    return new Map(
      (Array.isArray(mealsSnapshot?.docs) ? mealsSnapshot.docs : [])
        .filter((mealSnapshot) => mealSnapshot?.id)
        .map((mealSnapshot) => {
          const normalizedMealId = String(mealSnapshot.id).trim().toLowerCase();
          return [
            normalizedMealId,
            {
              id: normalizedMealId,
              ...mealSnapshot.data(),
            },
          ];
        }),
    );
  }

  upsertFoodEntity(record, options = {}) {
    const normalizedId = normalizeId(record?.id, "food");
    const existingEntity = this.foodEntitiesById.get(normalizedId);
    const normalizedRecord = {
      ...record,
      id: normalizedId,
    };

    if (existingEntity) {
      if (
        existingEntity.tryApplyNoRefreshServerAck(normalizedRecord, {
          coach: this.coach,
          finalizePendingAck: options.source !== "commit",
        })
      ) {
        return existingEntity;
      }
      existingEntity.applyServerRecord(normalizedRecord, { coach: this.coach });
      if (options.source !== "commit") {
        existingEntity.finalizePendingServerAckAfterMerge(normalizedRecord?._rev);
      }
      return existingEntity;
    }

    const nextEntity = this.hydrateFoodRecord(normalizedRecord);
    this.foodEntitiesById.set(normalizedId, nextEntity);
    return nextEntity;
  }

  upsertRecipeEntity(record, options = {}) {
    const normalizedId = normalizeId(record?.id, "recipe");
    const existingEntity = this.recipeEntitiesById.get(normalizedId);
    const normalizedRecord = {
      ...record,
      id: normalizedId,
    };

    if (existingEntity) {
      if (
        existingEntity.tryApplyNoRefreshServerAck(normalizedRecord, {
          finalizePendingAck: options.source !== "commit",
        })
      ) {
        return existingEntity;
      }
      existingEntity.applyServerRecord(normalizedRecord);
      if (options.source !== "commit") {
        existingEntity.finalizePendingServerAckAfterMerge(normalizedRecord?._rev);
      }
      return existingEntity;
    }

    const nextEntity = this.hydrateRecipeRecord(normalizedRecord);
    this.recipeEntitiesById.set(normalizedId, nextEntity);
    return nextEntity;
  }

  upsertEquivalentEntity(record, options = {}) {
    const normalizedId = normalizeId(record?.id, "equivalent");
    const existingEntity = this.equivalentEntitiesById.get(normalizedId);
    const normalizedRecord = {
      ...record,
      id: normalizedId,
    };

    if (existingEntity) {
      if (
        existingEntity.tryApplyNoRefreshServerAck(normalizedRecord, {
          finalizePendingAck: options.source !== "commit",
        })
      ) {
        return existingEntity;
      }
      existingEntity.applyServerRecord(normalizedRecord);
      if (options.source !== "commit") {
        existingEntity.finalizePendingServerAckAfterMerge(normalizedRecord?._rev);
      }
      return existingEntity;
    }

    const nextEntity = this.hydrateEquivalentRecord(normalizedRecord);
    this.equivalentEntitiesById.set(normalizedId, nextEntity);
    return nextEntity;
  }

  upsertPlanEntity(planRecord, mealRecords = null, options = {}) {
    const normalizedId = normalizeId(planRecord?.id, "plan");
    const existingEntity = this.planEntitiesById.get(normalizedId);
    const normalizedPlanRecord = {
      ...planRecord,
      id: normalizedId,
      createdByMe: planRecord?.createdByMe !== false,
    };

    if (existingEntity) {
      if (
        existingEntity.tryApplyNoRefreshServerAck(normalizedPlanRecord, {
          mealRecords,
          mealsHydrated: options.mealsHydrated ?? Array.isArray(mealRecords),
          finalizePendingAck: options.source !== "commit",
        })
      ) {
        return existingEntity;
      }
      existingEntity.applyServerRecord(normalizedPlanRecord, mealRecords);
      if (options.source !== "commit") {
        existingEntity.finalizePendingServerAckAfterMerge(normalizedPlanRecord?._rev);
      }
      return existingEntity;
    }

    const nextEntity = this.hydratePlanRecord(
      normalizedPlanRecord,
      mealRecords ?? [],
      {
        mealsHydrated: options.mealsHydrated ?? Array.isArray(mealRecords),
      },
    );
    this.planEntitiesById.set(normalizedId, nextEntity);
    return nextEntity;
  }

  removeEntity(entityType, entityId) {
    const entityMap = this.getEntityMap(entityType);
    const normalizedId = normalizeId(entityId, entityType);
    const existingEntity = entityMap?.get(normalizedId);

    if (!existingEntity) {
      return;
    }

    if (existingEntity.hasUnsyncedChanges) {
      existingEntity.markDeletedRemotely?.();
      return;
    }

    existingEntity.dispose?.();
    entityMap.delete(normalizedId);
  }

  discardRemoteDeletedEntity(entity) {
    const entityType = String(entity?.entityType ?? "").trim();
    const normalizedId = normalizeId(entity?.id, entityType);

    if (entityType === "plan") {
      this.planRootRecordsById.delete(normalizedId);
      this.planMealsByPlanId.delete(normalizedId);
    }

    const entityMap = this.getEntityMap(entityType);
    const trackedEntity = entityMap?.get(normalizedId);
    if (trackedEntity === entity) {
      entity.dispose?.();
      entityMap.delete(normalizedId);
    }
  }

  queueEntitySave(entity) {
    if (!entity?.id || entity?.isPersistable !== true || !entity.hasUnsyncedChanges) {
      return Promise.resolve(false);
    }

    const entityType = String(entity.entityType ?? "").trim();
    const entityKey = createEntityKey(entityType, entity.id);
    const submittedVersion = entity.localVersion;

    return new Promise((resolve) => {
      this.syncController.enqueueMutation({
        entityKey,
        execute: () => this.persistEntity(entityType, entity, { submittedVersion }),
        onQueued: ({ offline }) => {
          entity.noteMutationQueued({ offline });
          if (offline) {
            resolve({
              ok: false,
              kind: "offline_pending",
              queued: true,
            });
          }
        },
        onSuccess: (result, context = {}) => {
          resolve({
            ok: true,
            queued: false,
            superseded: context.superseded === true,
            result,
          });
        },
        onBlocked: (error) => {
          entity.noteMutationBlocked(error);
          resolve({
            ok: false,
            queued: false,
            kind: SyncErrorKind.BLOCKED,
            error,
          });
        },
        onError: (error, context = {}) => {
          entity.noteMutationError(error, {
            kind: context.kind,
            offline: !this.syncController.isOnline,
          });
          resolve({
            ok: false,
            queued: context.kind !== SyncErrorKind.FATAL,
            kind: context.kind ?? "retryable",
            error,
          });
        },
      });
    });
  }

  createDraftFood(initialData = {}) {
    const draftFood = new NutritionFood(
      {
        id: normalizeId(initialData?.id, uuidv4()),
        ...initialData,
      },
      {
        coach: this.coach,
        store: this,
      },
    );
    this.foodEntitiesById.set(draftFood.id, draftFood);
    return draftFood;
  }

  createDraftRecipe(initialData = {}) {
    const createdAt = initialData?.createdAt ?? new Date();
    const draftRecipe = new NutritionRecipe(
      {
        id: normalizeId(initialData?.id, uuidv4()),
        createdAt,
        ...initialData,
      },
      { store: this },
    );
    this.recipeEntitiesById.set(draftRecipe.id, draftRecipe);
    return draftRecipe;
  }

  createDraftEquivalent(initialData = {}) {
    const createdAt = initialData?.createdAt ?? new Date();
    const draftEquivalent = new NutritionEquivalent(
      {
        id: normalizeId(initialData?.id, uuidv4()),
        createdAt,
        ...initialData,
      },
      { store: this },
    );
    this.equivalentEntitiesById.set(draftEquivalent.id, draftEquivalent);
    return draftEquivalent;
  }

  createDraftPlan(initialData = {}) {
    const createdAt = initialData?.createdAt ?? new Date();
    const draftPlan = new NutritionPlan(
      {
        id: normalizeId(initialData?.id, uuidv4()),
        createdAt,
        ...initialData,
      },
      {
        store: this,
        mealsHydrated: true,
      },
    );
    this.planEntitiesById.set(draftPlan.id, draftPlan);
    return draftPlan;
  }

  ensureEntityHasUnsyncedDraftForImmediateSave(entity, fallbackTouchedPaths = ["name"]) {
    if (!entity || entity.hasUnsyncedChanges) {
      return false;
    }

    const snapshot =
      typeof entity?.getDraftSnapshot === "function" ? entity.getDraftSnapshot() : entity?.draft;
    if (!snapshot || typeof entity?.applyDraft !== "function") {
      return false;
    }

    const touchedPaths = Object.keys(snapshot).filter(Boolean);
    entity.applyDraft(snapshot, {
      scheduleSave: false,
      touchedPaths: touchedPaths.length > 0 ? touchedPaths : fallbackTouchedPaths,
    });
    return true;
  }

  async persistTransientEntityOnClose(entity, fallbackTouchedPaths = ["name"]) {
    if (!entity || entity.isPersistable !== true) {
      return false;
    }

    this.ensureEntityHasUnsyncedDraftForImmediateSave(entity, fallbackTouchedPaths);
    return entity.saveNow?.() ?? false;
  }

  revertPersistedInvalidDraftOnClose(entityType, entityId) {
    const normalizedId = normalizeId(entityId, entityType);
    const entity = this.getEntityMap(entityType)?.get(normalizedId);

    if (
      !entity ||
      Number(entity?._rev ?? 0) <= 0 ||
      entity.serverBase == null ||
      entity.hasUnsyncedChanges !== true ||
      entity.isPersistable === true
    ) {
      return false;
    }

    entity.discardLocalChanges();
    return true;
  }

  async finalizeEntityEditorClose(
    entityType,
    entityId,
    {
      isSemanticallyEmptyDraft = () => true,
      deleteEntity = null,
      fallbackTouchedPaths = ["name"],
    } = {},
  ) {
    const normalizedId = normalizeId(entityId, entityType);
    const entity = this.getEntityMap(entityType)?.get(normalizedId);

    if (!entity) {
      return { action: "missing" };
    }

    const rev = Number(entity?._rev ?? 0) || 0;
    const hasServerBase = entity.serverBase != null;
    const hasUnsyncedChanges = entity.hasUnsyncedChanges === true;
    const isPersistable = entity.isPersistable === true;
    const isTransientDraft = rev <= 0 && !hasServerBase;
    const draftSnapshot =
      typeof entity?.getDraftSnapshot === "function" ? entity.getDraftSnapshot() : entity?.draft;
    const isInvalidPersistedDraft =
      rev > 0 && hasServerBase && hasUnsyncedChanges && !isPersistable;
    const isEmptyPersistedDraft =
      isInvalidPersistedDraft && isSemanticallyEmptyDraft(draftSnapshot);

    let action = "keep";

    if (isTransientDraft && isSemanticallyEmptyDraft(draftSnapshot)) {
      action = "remove_transient";
      this.removeTransientEntity(entityType, normalizedId);
    } else if (isTransientDraft && isPersistable) {
      await this.persistTransientEntityOnClose(entity, fallbackTouchedPaths);
    } else if (isEmptyPersistedDraft) {
      action = "delete_empty_persisted";
    } else if (isInvalidPersistedDraft) {
      action = "revert_invalid_persisted";
      this.revertPersistedInvalidDraftOnClose(entityType, normalizedId);
    }

    if (action !== "delete_empty_persisted" || typeof deleteEntity !== "function") {
      return { action };
    }

    await deleteEntity(normalizedId);
    const trackedEntity = this.getEntityMap(entityType)?.get(normalizedId);
    if (trackedEntity?.deletedRemotely) {
      trackedEntity.discardLocalChanges();
    }
    return { action };
  }

  async finalizeRecipeEditorClose(recipeId) {
    return this.finalizeEntityEditorClose("recipe", recipeId, {
      isSemanticallyEmptyDraft: isRecipeDraftSemanticallyEmpty,
      deleteEntity: (normalizedId) => this.deleteRecipe(normalizedId),
    });
  }

  async finalizeEquivalentEditorClose(equivalentId) {
    return this.finalizeEntityEditorClose("equivalent", equivalentId, {
      isSemanticallyEmptyDraft: isEquivalentDraftSemanticallyEmpty,
      deleteEntity: (normalizedId) => this.deleteEquivalent(normalizedId),
    });
  }

  async finalizePlanEditorClose(planId) {
    const result = await this.finalizeEntityEditorClose("plan", planId, {
      isSemanticallyEmptyDraft: isPlanDraftSemanticallyEmpty,
      deleteEntity: (normalizedId) => this.deletePlan(normalizedId),
      fallbackTouchedPaths: ["name", "notes", "goals", "meals"],
    });
    if (result.action !== "delete_empty_persisted") {
      return result;
    }

    const normalizedId = normalizeId(planId, "plan");
    const trackedEntity = this.planEntitiesById.get(normalizedId);
    if (trackedEntity?.deletedRemotely) {
      trackedEntity.discardLocalChanges();
    }

    return result;
  }

  revertPersistedPlanInvalidDraftOnClose(planId) {
    return this.revertPersistedInvalidDraftOnClose("plan", planId);
  }

  removeTransientEntity(entityType, entityId) {
    const entityMap = this.getEntityMap(entityType);
    const normalizedId = normalizeId(entityId, entityType);
    const entity = entityMap?.get(normalizedId);

    if (!entity) {
      return;
    }

    if (Number(entity?._rev ?? 0) > 0 || entity.serverBase != null) {
      return;
    }

    entity.dispose?.();
    entityMap.delete(normalizedId);
  }

  buildPlanRootRecord(planData, mealRecords = [], options = {}) {
    const plan = new CoachNutritionPlan({
      ...planData,
      meals: mealRecords,
    });

    return plan.toPlanDoc({
      createdAt: options.createdAt,
      updatedAt: options.updatedAt,
      rev: options.rev,
      mealCount: mealRecords.length,
      summaryTotals: plan.totals,
    });
  }

  buildPlanMealRecord(mealData) {
    return new CoachNutritionPlanMeal(mealData).toMealDoc();
  }

  getPlanMealsMap(planId) {
    return this.planMealsByPlanId.get(normalizeId(planId, "plan")) ?? null;
  }

  async loadPlanMealRecords(planId, options = {}) {
    if (!this.coach?.id || !planId) {
      return [];
    }

    const normalizedPlanId = normalizeId(planId, "plan");
    if (options?.force !== true && this.planMealsByPlanId.has(normalizedPlanId)) {
      return sortMealsByOrder(Array.from(this.planMealsByPlanId.get(normalizedPlanId)?.values() ?? []));
    }

    const mealsSnapshot = await getDocs(
      buildPlanMealsQuery(this.coach.id, normalizedPlanId),
    );

    return sortMealsByOrder(
      mealsSnapshot.docs.map((mealDoc) => ({
        id: mealDoc.id,
        ...mealDoc.data(),
      })),
    );
  }

  updatePlanCache(planId, planRecord = null, mealRecords = null, options = {}) {
    const normalizedPlanId = normalizeId(planId, "plan");
    const cachedMealRecords = this.planMealsByPlanId.has(normalizedPlanId)
      ? sortMealsByOrder(
          Array.from(this.planMealsByPlanId.get(normalizedPlanId)?.values() ?? []),
        )
      : null;
    const resolvedMealRecords = mealRecords ?? cachedMealRecords;
    const mealsHydrated = resolvedMealRecords !== null;

    if (planRecord) {
      const normalizedPlanRecord = {
        id: normalizedPlanId,
        ...planRecord,
      };
      this.planRootRecordsById.set(normalizedPlanId, normalizedPlanRecord);
      this.upsertPlanEntity(normalizedPlanRecord, resolvedMealRecords, {
        mealsHydrated,
        source: options.source,
      });
    } else {
      this.planRootRecordsById.delete(normalizedPlanId);
    }

    if (mealRecords !== null) {
      this.planMealsByPlanId.set(
        normalizedPlanId,
        new Map(
          sortMealsByOrder(mealRecords).map((mealRecord) => [
            String(mealRecord?.id ?? "").trim().toLowerCase(),
            mealRecord,
          ]),
        ),
      );
    }
    if (planRecord) {
      runInAction(() => {
        this.hasFetchedPlans = true;
      });
      return this.planEntitiesById.get(normalizedPlanId) ?? null;
    }

    return null;
  }

  replacePlanListItem(planRecord, mealRecords = null) {
    if (!planRecord?.id) {
      return null;
    }

    return this.updatePlanCache(planRecord.id, planRecord, mealRecords);
  }

  removePlanFromStore(planId) {
    const normalizedPlanId = normalizeId(planId, "plan");
    const entity = this.planEntitiesById.get(normalizedPlanId);

    if (entity?.hasUnsyncedChanges) {
      entity.markDeletedRemotely?.();
    } else {
      entity?.dispose?.();
      this.planEntitiesById.delete(normalizedPlanId);
    }

    this.planRootRecordsById.delete(normalizedPlanId);
    this.planMealsByPlanId.delete(normalizedPlanId);
  }

  async ensureRootDocument() {
    if (!this.coach?.id) {
      throw createFatalSyncError("Cannot initialize nutrition without a coach id");
    }

    const rootDocRef = doc(db, ...buildNutritionRootDocPath(this.coach.id));
    await setDoc(
      rootDocRef,
      {
        schema_version: "nutrition_v1",
        storage_scope: "coach_only",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  async fetchFromCloud(options = {}) {
    await this.ensureResourcesFromCloud(COACH_NUTRITION_RESOURCE_ORDER, options);

    return {
      foods: this.sortedFoods,
      recipes: this.sortedRecipes,
      equivalents: this.sortedEquivalents,
      plans: this.sortedPlans,
    };
  }

  async ensureResourcesFromCloud(resourceKeys = COACH_NUTRITION_RESOURCE_ORDER, options = {}) {
    const normalizedResourceKeys = normalizeCoachNutritionResourceKeys(resourceKeys);

    await Promise.all(
      normalizedResourceKeys.map((resourceKey) => {
        if (resourceKey === COACH_NUTRITION_RESOURCE_FOODS) {
          return this.fetchFoodsFromCloud(options);
        }

        if (resourceKey === COACH_NUTRITION_RESOURCE_RECIPES) {
          return this.fetchRecipesFromCloud(options);
        }

        if (resourceKey === COACH_NUTRITION_RESOURCE_EQUIVALENTS) {
          return this.fetchEquivalentsFromCloud(options);
        }

        if (resourceKey === COACH_NUTRITION_RESOURCE_PLANS) {
          return this.fetchPlansFromCloud(options);
        }

        return Promise.resolve([]);
      }),
    );

    return {
      foods: this.sortedFoods,
      recipes: this.sortedRecipes,
      equivalents: this.sortedEquivalents,
      plans: this.sortedPlans,
    };
  }

  stopSync() {
    [
      this.foodsReadKey,
      this.recipesReadKey,
      this.equivalentsReadKey,
      this.plansReadKey,
    ].forEach((readKey) => this.syncController.unregisterRead(readKey));

    Array.from(this.syncController.reads.keys())
      .filter((readKey) => isPlanReadKey(readKey))
      .forEach((readKey) => this.syncController.unregisterRead(readKey));

    this.planRootRecordsById.clear();
    this.planMealsByPlanId.clear();
  }

  reset() {
    this.stopSync();
    this.foods.forEach((food) => food.dispose?.());
    this.recipes.forEach((recipe) => recipe.dispose?.());
    this.equivalents.forEach((equivalent) => equivalent.dispose?.());
    this.plans.forEach((plan) => plan.dispose?.());
    this.syncController.dispose();
    this.syncController = new SyncDomainController("nutrition");

    runInAction(() => {
      this.foodEntitiesById.clear();
      this.recipeEntitiesById.clear();
      this.equivalentEntitiesById.clear();
      this.planEntitiesById.clear();
      this.isFetchingFoods = false;
      this.isFetchingRecipes = false;
      this.isFetchingEquivalents = false;
      this.isFetchingPlans = false;
      this.hasFetchedFoods = false;
      this.hasFetchedRecipes = false;
      this.hasFetchedEquivalents = false;
      this.hasFetchedPlans = false;
    });
  }

  async fetchFoodsFromCloud({ force = false } = {}) {
    if (!this.coach?.id) {
      return [];
    }

    if (force) {
      this.syncController.unregisterRead(this.foodsReadKey);
    } else if (this.syncController.reads.has(this.foodsReadKey)) {
      return this.sortedFoods;
    }

    runInAction(() => {
      this.isFetchingFoods = true;
    });

    await this.syncController.registerRead(this.foodsReadKey, this.startFoodsRead);
    return this.sortedFoods;
  }

  startFoodsRead({ onReady, onError }) {
    let isInitialSnapshot = true;

    return onSnapshot(
      collection(db, ...buildNutritionFoodsCollectionPath(this.coach.id)),
      (foodsSnapshot) => {
        runInAction(() => {
          if (isInitialSnapshot) {
            this.reconcileInitialEntitySnapshot(
              "food",
              foodsSnapshot,
              (docSnapshot) => ({
                id: docSnapshot.id,
                ...docSnapshot.data(),
              }),
              (record) => this.upsertFoodEntity(record),
            );
            isInitialSnapshot = false;
            this.hasFetchedFoods = true;
            this.isFetchingFoods = false;
            return;
          }

          collectDocs(foodsSnapshot).forEach((change) => {
            const docSnapshot = change?.doc;
            if (!docSnapshot?.id) {
              return;
            }

            if (change.type === "removed") {
              this.removeEntity("food", docSnapshot.id);
              return;
            }

            this.upsertFoodEntity({
              id: docSnapshot.id,
              ...docSnapshot.data(),
            });
          });
          this.hasFetchedFoods = true;
          this.isFetchingFoods = false;
        });
        onReady();
      },
      (error) => {
        runInAction(() => {
          this.isFetchingFoods = false;
        });
        onError(error);
      },
    );
  }

  async persistEntity(entityType, entity, options = {}) {
    if (!this.coach?.id) {
      throw createFatalSyncError(`Cannot save nutrition ${entityType} without a coach id`);
    }

    await this.ensureRootDocument();

    if (entityType === "food") {
      return this.persistFoodEntity(entity, options);
    }

    if (entityType === "recipe") {
      return this.persistRecipeEntity(entity, options);
    }

    if (entityType === "equivalent") {
      return this.persistEquivalentEntity(entity, options);
    }

    if (entityType === "plan") {
      return this.persistPlanEntity(entity, options);
    }

    throw createFatalSyncError(`Unsupported nutrition entity type: ${entityType}`);
  }

  async persistFoodEntity(entity, options = {}) {
    const resolvedFoodId = normalizeId(entity?.id, "food");
    const foodDocRef = doc(
      db,
      ...buildNutritionFoodDocPath(this.coach.id, resolvedFoodId),
    );

    let foodRecord = null;
    let mergedDraft = null;
    await runTransaction(db, async (transaction) => {
      const foodSnapshot = await transaction.get(foodDocRef);
      const existingFood = foodSnapshot.exists() ? foodSnapshot.data() : null;
      const nextRev = Number(existingFood?._rev ?? 0) + 1;
      mergedDraft = foodSnapshot.exists()
        ? mergeServerIntoLocalByTouchedPaths(
            NutritionFood.buildDraftFromRecord(existingFood, {
              coach: this.coach,
            }),
            entity.getDraftSnapshot(),
            entity.touchedPaths,
          )
        : applyQueuedDraftToServerRecord(entity);
      foodRecord = NutritionFood.buildRecordFromDraft(mergedDraft, {
        id: resolvedFoodId,
        origin: entity.origin,
        brand: entity.brand,
        store: entity.store,
        createdAt: existingFood?.createdAt ?? serverTimestamp(),
        updatedAt: serverTimestamp(),
        rev: nextRev,
      });

      transaction.set(
        foodDocRef,
        {
          ...foodRecord,
          order: deleteField(),
        },
        { merge: true },
      );
    });

    runInAction(() => {
      entity.noteMutationSuccess({
        submittedSnapshot: mergedDraft ?? entity.getDraftSnapshot(),
        submittedVersion: options.submittedVersion,
        nextRev: foodRecord?._rev,
      });
      this.upsertFoodEntity(foodRecord, { source: "commit" });
      this.hasFetchedFoods = true;
    });

    return {
      ...foodRecord,
      submittedDraft: mergedDraft ?? entity.getDraftSnapshot(),
    };
  }

  async persistRecipeEntity(entity, options = {}) {
    const resolvedRecipeId = normalizeId(entity?.id, "recipe");
    const recipeDocRef = doc(
      db,
      ...buildNutritionRecipeDocPath(this.coach.id, resolvedRecipeId),
    );

    let recipeRecord = null;
    let mergedDraft = null;
    await runTransaction(db, async (transaction) => {
      const recipeSnapshot = await transaction.get(recipeDocRef);
      const existingRecipe = recipeSnapshot.exists() ? recipeSnapshot.data() : null;
      const nextRev = Number(existingRecipe?._rev ?? 0) + 1;
      mergedDraft = recipeSnapshot.exists()
        ? mergeServerIntoLocalByTouchedPaths(
            NutritionRecipe.buildDraftFromRecord(existingRecipe),
            entity.getDraftSnapshot(),
            entity.touchedPaths,
          )
        : applyQueuedDraftToServerRecord(entity);
      recipeRecord = NutritionRecipe.buildRecordFromDraft(mergedDraft, {
        id: resolvedRecipeId,
        order: entity.order,
        origin: entity.origin,
        createdAt: existingRecipe?.createdAt ?? entity.createdAt ?? serverTimestamp(),
        updatedAt: serverTimestamp(),
        rev: nextRev,
      });

      transaction.set(recipeDocRef, recipeRecord);
    });

    runInAction(() => {
      entity.noteMutationSuccess({
        submittedSnapshot: mergedDraft ?? entity.getDraftSnapshot(),
        submittedVersion: options.submittedVersion,
        nextRev: recipeRecord?._rev,
      });
      this.upsertRecipeEntity(recipeRecord, { source: "commit" });
      this.hasFetchedRecipes = true;
    });

    return {
      ...recipeRecord,
      submittedDraft: mergedDraft ?? entity.getDraftSnapshot(),
    };
  }

  async persistEquivalentEntity(entity, options = {}) {
    const resolvedEquivalentId = normalizeId(entity?.id, "equivalent");
    const equivalentDocRef = doc(
      db,
      ...buildNutritionEquivalentDocPath(this.coach.id, resolvedEquivalentId),
    );

    let equivalentRecord = null;
    let mergedDraft = null;
    await runTransaction(db, async (transaction) => {
      const equivalentSnapshot = await transaction.get(equivalentDocRef);
      const existingEquivalent = equivalentSnapshot.exists()
        ? equivalentSnapshot.data()
        : null;
      const nextRev = Number(existingEquivalent?._rev ?? 0) + 1;
      mergedDraft = equivalentSnapshot.exists()
        ? mergeServerIntoLocalByTouchedPaths(
            NutritionEquivalent.buildDraftFromRecord(existingEquivalent),
            entity.getDraftSnapshot(),
            entity.touchedPaths,
          )
        : applyQueuedDraftToServerRecord(entity);
      equivalentRecord = NutritionEquivalent.buildRecordFromDraft(mergedDraft, {
        id: resolvedEquivalentId,
        origin: entity.origin,
        createdAt: existingEquivalent?.createdAt ?? entity.createdAt ?? serverTimestamp(),
        updatedAt: serverTimestamp(),
        _rev: nextRev,
        rev: nextRev,
      });

      transaction.set(equivalentDocRef, equivalentRecord);
    });

    runInAction(() => {
      entity.noteMutationSuccess({
        submittedSnapshot: mergedDraft ?? entity.getDraftSnapshot(),
        submittedVersion: options.submittedVersion,
        nextRev: equivalentRecord?._rev,
      });
      this.upsertEquivalentEntity(equivalentRecord, { source: "commit" });
      this.hasFetchedEquivalents = true;
    });

    return {
      ...equivalentRecord,
      submittedDraft: mergedDraft ?? entity.getDraftSnapshot(),
    };
  }

  async persistFood(foodOrData, options = {}) {
    const foodEntity =
      foodOrData instanceof NutritionFood
        ? foodOrData
        : new NutritionFood(foodOrData, {
            coach: this.coach,
            store: this,
          });
    return this.persistFoodEntity(foodEntity, options);
  }

  saveFood(foodData, options = {}) {
    return this.persistFood(foodData, options);
  }

  async persistFoodDeletion(foodId) {
    if (!this.coach?.id || !foodId) {
      return;
    }

    const normalizedFoodId = normalizeId(foodId);
    await deleteDoc(doc(db, ...buildNutritionFoodDocPath(this.coach.id, normalizedFoodId)));

    runInAction(() => {
      this.removeEntity("food", normalizedFoodId);
    });
  }

  deleteFood(foodId) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("food", foodId),
      execute: () => this.persistFoodDeletion(foodId),
    });
  }

  async fetchRecipesFromCloud({ force = false } = {}) {
    if (!this.coach?.id) {
      return [];
    }

    if (force) {
      this.syncController.unregisterRead(this.recipesReadKey);
    } else if (this.syncController.reads.has(this.recipesReadKey)) {
      return this.sortedRecipes;
    }

    runInAction(() => {
      this.isFetchingRecipes = true;
    });

    await this.syncController.registerRead(this.recipesReadKey, this.startRecipesRead);
    return this.sortedRecipes;
  }

  startRecipesRead({ onReady, onError }) {
    let isInitialSnapshot = true;

    return onSnapshot(
      collection(db, ...buildNutritionRecipesCollectionPath(this.coach.id)),
      (recipesSnapshot) => {
        runInAction(() => {
          if (isInitialSnapshot) {
            this.reconcileInitialEntitySnapshot(
              "recipe",
              recipesSnapshot,
              (docSnapshot) => ({
                id: docSnapshot.id,
                ...docSnapshot.data(),
              }),
              (record) => this.upsertRecipeEntity(record),
            );
            isInitialSnapshot = false;
            this.hasFetchedRecipes = true;
            this.isFetchingRecipes = false;
            return;
          }

          collectDocs(recipesSnapshot).forEach((change) => {
            const docSnapshot = change?.doc;
            if (!docSnapshot?.id) {
              return;
            }

            if (change.type === "removed") {
              this.removeEntity("recipe", docSnapshot.id);
              return;
            }

            this.upsertRecipeEntity({
              id: docSnapshot.id,
              ...docSnapshot.data(),
            });
          });
          this.hasFetchedRecipes = true;
          this.isFetchingRecipes = false;
        });
        onReady();
      },
      (error) => {
        runInAction(() => {
          this.isFetchingRecipes = false;
        });
        onError(error);
      },
    );
  }

  async persistRecipe(recipeOrData, options = {}) {
    const recipeEntity =
      recipeOrData instanceof NutritionRecipe
        ? recipeOrData
        : new NutritionRecipe(recipeOrData, { store: this });
    return this.persistRecipeEntity(recipeEntity, options);
  }

  saveRecipe(recipeData, options = {}) {
    return this.persistRecipe(recipeData, options);
  }

  async persistRecipeDeletion(recipeId) {
    if (!this.coach?.id || !recipeId) {
      return;
    }

    const normalizedRecipeId = normalizeId(recipeId);
    await deleteDoc(doc(db, ...buildNutritionRecipeDocPath(this.coach.id, normalizedRecipeId)));

    runInAction(() => {
      this.removeEntity("recipe", normalizedRecipeId);
    });
  }

  deleteRecipe(recipeId) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("recipe", recipeId),
      execute: () => this.persistRecipeDeletion(recipeId),
    });
  }

  async fetchEquivalentsFromCloud({ force = false } = {}) {
    if (!this.coach?.id) {
      return [];
    }

    if (force) {
      this.syncController.unregisterRead(this.equivalentsReadKey);
    } else if (this.syncController.reads.has(this.equivalentsReadKey)) {
      return this.sortedEquivalents;
    }

    runInAction(() => {
      this.isFetchingEquivalents = true;
    });

    await this.syncController.registerRead(
      this.equivalentsReadKey,
      this.startEquivalentsRead,
    );
    return this.sortedEquivalents;
  }

  startEquivalentsRead({ onReady, onError }) {
    let isInitialSnapshot = true;

    return onSnapshot(
      collection(db, ...buildNutritionEquivalentsCollectionPath(this.coach.id)),
      (equivalentsSnapshot) => {
        runInAction(() => {
          if (isInitialSnapshot) {
            this.reconcileInitialEntitySnapshot(
              "equivalent",
              equivalentsSnapshot,
              (docSnapshot) => ({
                id: docSnapshot.id,
                ...docSnapshot.data(),
              }),
              (record) => this.upsertEquivalentEntity(record),
            );
            isInitialSnapshot = false;
            this.hasFetchedEquivalents = true;
            this.isFetchingEquivalents = false;
            return;
          }

          collectDocs(equivalentsSnapshot).forEach((change) => {
            const docSnapshot = change?.doc;
            if (!docSnapshot?.id) {
              return;
            }

            if (change.type === "removed") {
              this.removeEntity("equivalent", docSnapshot.id);
              return;
            }

            this.upsertEquivalentEntity({
              id: docSnapshot.id,
              ...docSnapshot.data(),
            });
          });
          this.hasFetchedEquivalents = true;
          this.isFetchingEquivalents = false;
        });
        onReady();
      },
      (error) => {
        runInAction(() => {
          this.isFetchingEquivalents = false;
        });
        onError(error);
      },
    );
  }

  async persistEquivalent(equivalentOrData, options = {}) {
    const equivalentEntity =
      equivalentOrData instanceof NutritionEquivalent
        ? equivalentOrData
        : new NutritionEquivalent(equivalentOrData, { store: this });
    return this.persistEquivalentEntity(equivalentEntity, options);
  }

  saveEquivalent(equivalentData, options = {}) {
    return this.persistEquivalent(equivalentData, options);
  }

  async persistEquivalentDeletion(equivalentId) {
    if (!this.coach?.id || !equivalentId) {
      return;
    }

    const normalizedEquivalentId = normalizeId(equivalentId);
    await deleteDoc(
      doc(db, ...buildNutritionEquivalentDocPath(this.coach.id, normalizedEquivalentId)),
    );

    runInAction(() => {
      this.removeEntity("equivalent", normalizedEquivalentId);
    });
  }

  deleteEquivalent(equivalentId) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("equivalent", equivalentId),
      execute: () => this.persistEquivalentDeletion(equivalentId),
    });
  }

  async persistPlanEntity(entity, options = {}) {
    const resolvedPlanId = normalizeId(entity?.id, "plan");
    const draftAtPersistStart = entity.getDraftSnapshot();
    const result = await this.persistResolvedPlanDraft(
      resolvedPlanId,
      () => entity.getDraftSnapshot(),
      {
        baseRev: entity.baseRev,
        createdAt: entity.createdAt,
        touchedPaths: entity.touchedPaths,
        submittedVersion: options.submittedVersion,
        entity,
        mergeRootWrite: false,
      },
    );

    return {
      _rev: Number(result?._rev ?? entity?._rev ?? 0) || 0,
      submittedDraft: result?.submittedDraft ?? draftAtPersistStart,
    };
  }

  async fetchPlansFromCloud({ force = false } = {}) {
    if (!this.coach?.id) {
      return [];
    }

    if (force) {
      this.syncController.unregisterRead(this.plansReadKey);
    } else if (this.syncController.reads.has(this.plansReadKey)) {
      return this.sortedPlans;
    }

    runInAction(() => {
      this.isFetchingPlans = true;
    });

    await this.syncController.registerRead(this.plansReadKey, this.startPlansRead);
    return this.sortedPlans;
  }

  startPlansRead({ onReady, onError }) {
    let isInitialSnapshot = true;

    return onSnapshot(
      collection(db, ...buildNutritionPlansCollectionPath(this.coach.id)),
      (plansSnapshot) => {
        runInAction(() => {
          if (isInitialSnapshot) {
            this.reconcileInitialPlansSnapshot(plansSnapshot);
            isInitialSnapshot = false;
            this.hasFetchedPlans = true;
            this.isFetchingPlans = false;
            return;
          }

          collectDocs(plansSnapshot).forEach((change) => {
            const docSnapshot = change?.doc;
            if (!docSnapshot?.id) {
              return;
            }

            if (change.type === "removed") {
              this.removePlanFromStore(docSnapshot.id);
              return;
            }

            this.updatePlanCache(
              docSnapshot.id,
              {
                id: docSnapshot.id,
                ...docSnapshot.data(),
              },
              null,
            );
          });
          this.hasFetchedPlans = true;
          this.isFetchingPlans = false;
        });
        onReady();
      },
      (error) => {
        runInAction(() => {
          this.isFetchingPlans = false;
        });
        onError(error);
      },
    );
  }

  async watchPlan(planId, onPlanChange = null) {
    if (!this.coach?.id || !planId) {
      return () => {};
    }

    const normalizedPlanId = normalizeId(planId, "plan");
    const rootReadKey = createPlanReadKey(normalizedPlanId, "root");
    const mealsReadKey = createPlanReadKey(normalizedPlanId, "meals");
    let isInitialMealsSnapshot = true;
    const emitPlan =
      typeof onPlanChange === "function"
        ? () => {
            onPlanChange(this.planEntitiesById.get(normalizedPlanId) ?? null);
          }
        : noop;

    await Promise.all([
      this.syncController.registerRead(rootReadKey, ({ onReady, onError }) =>
        onSnapshot(
          doc(db, ...buildNutritionPlanDocPath(this.coach.id, normalizedPlanId)),
          (planSnapshot) => {
            runInAction(() => {
              if (planSnapshot.exists()) {
                this.updatePlanCache(normalizedPlanId, {
                  id: planSnapshot.id,
                  ...planSnapshot.data(),
                });
              } else {
                this.removePlanFromStore(normalizedPlanId);
              }
            });
            emitPlan();
            onReady();
          },
          onError,
        ),
      ),
      this.syncController.registerRead(mealsReadKey, ({ onReady, onError }) =>
        onSnapshot(
          buildPlanMealsQuery(this.coach.id, normalizedPlanId),
          (mealsSnapshot) => {
            runInAction(() => {
              const cachedMealsById = new Map(
                this.planMealsByPlanId.get(normalizedPlanId) ?? [],
              );
              let nextMealsById = isInitialMealsSnapshot
                ? this.buildMealsMapFromSnapshot(mealsSnapshot)
                : new Map(cachedMealsById);

              if (isInitialMealsSnapshot && nextMealsById.size === 0) {
                const rootRecord = this.planRootRecordsById.get(normalizedPlanId);
                const entity = this.planEntitiesById.get(normalizedPlanId);
                const rootRev = Number(rootRecord?._rev ?? entity?._rev ?? 0) || 0;
                const shouldKeepCachedMeals =
                  cachedMealsById.size > 0 &&
                  Number(rootRecord?.meal_count ?? 0) > 0 &&
                  entity?.hasPendingServerAck?.(rootRev) === true;

                // A self-ack can briefly surface an empty first meals snapshot before the
                // committed meal docs arrive. Preserve the cached committed meals in that case
                // so the open editor does not rebuild into fresh starter meals mid-session.
                if (shouldKeepCachedMeals) {
                  nextMealsById = cachedMealsById;
                }
              }

              if (!isInitialMealsSnapshot) {
                collectDocs(mealsSnapshot).forEach((change) => {
                  const mealSnapshot = change?.doc;
                  if (!mealSnapshot?.id) {
                    return;
                  }

                  const normalizedMealId = String(mealSnapshot.id).trim().toLowerCase();

                  if (change.type === "removed") {
                    nextMealsById.delete(normalizedMealId);
                    return;
                  }

                  nextMealsById.set(normalizedMealId, {
                    id: normalizedMealId,
                    ...mealSnapshot.data(),
                  });
                });
              }

              this.planMealsByPlanId.set(normalizedPlanId, nextMealsById);
              const rootRecord = this.planRootRecordsById.get(normalizedPlanId);
              if (rootRecord) {
                this.upsertPlanEntity(
                  rootRecord,
                  sortMealsByOrder(Array.from(nextMealsById.values())),
                  { mealsHydrated: true },
                );
              }

              isInitialMealsSnapshot = false;
            });
            emitPlan();
            onReady();
          },
          onError,
        ),
      ),
    ]);

    emitPlan();

    return () => {
      this.syncController.unregisterRead(rootReadKey);
      this.syncController.unregisterRead(mealsReadKey);
      this.planRootRecordsById.delete(normalizedPlanId);
      this.planMealsByPlanId.delete(normalizedPlanId);
    };
  }

  async persistResolvedPlanDraft(
    planId,
    buildLocalDraft,
    {
      baseRev,
      touchedPaths,
      requireExistingPlan = false,
      createdAt = null,
      submittedVersion,
      entity = null,
      mergeRootWrite = true,
    } = {},
  ) {
    if (!this.coach?.id || !planId) {
      throw createFatalSyncError("Cannot save nutrition plan without a plan id");
    }

    const normalizedPlanId = normalizeId(planId, "plan");
    const planDocRef = doc(db, ...buildNutritionPlanDocPath(this.coach.id, normalizedPlanId));
    const mealsQuery = buildPlanMealsQuery(this.coach.id, normalizedPlanId);

    await this.ensureRootDocument();

    let mergedDraft = null;
    let nextPlanDoc = null;
    let nextMealDocs = [];
    await runTransaction(db, async (transaction) => {
      const planSnapshot = await transaction.get(planDocRef);
      const mealsDiscoverySnapshot = await getDocs(mealsQuery);
      const mealDocSnaps = await Promise.all(
        mealsDiscoverySnapshot.docs.map((mealDoc) => transaction.get(mealDoc.ref)),
      );

      if (requireExistingPlan && !planSnapshot.exists()) {
        throw createFatalSyncError("Nutrition plan not found");
      }

      assertMatchingRevision(planSnapshot, baseRev);

      const existingPlanRecord = planSnapshot.exists()
        ? {
            id: normalizedPlanId,
            ...planSnapshot.data(),
          }
        : {
            id: normalizedPlanId,
          };
      const existingMealRecords = sortMealsByOrder(
        mealDocSnaps
          .filter((mealSnapshot) => mealSnapshot.exists())
          .map((mealSnapshot) => ({
            id: mealSnapshot.id,
            ...mealSnapshot.data(),
          })),
      );
      const serverDraft = buildPlanDraftForMerge(existingPlanRecord, existingMealRecords);
      const nextLocalDraftSource = clonePlainValue(serverDraft);
      const nextLocalDraftValue =
        typeof buildLocalDraft === "function"
          ? buildLocalDraft(nextLocalDraftSource, {
              planRecord: existingPlanRecord,
              mealRecords: existingMealRecords,
            })
          : nextLocalDraftSource;
      if (nextLocalDraftValue == null) {
        mergedDraft = null;
        nextPlanDoc = null;
        nextMealDocs = [];
        return;
      }
      const nextLocalDraft = clonePlainValue(nextLocalDraftValue ?? nextLocalDraftSource);
      const resolvedTouchedPaths =
        Array.isArray(touchedPaths) && touchedPaths.length > 0
          ? touchedPaths
          : diffTopLevelPaths(serverDraft, nextLocalDraft);
      const touchesMeals = resolvedTouchedPaths.includes("meals");

      mergedDraft = mergeServerIntoLocalByTouchedPaths(
        serverDraft,
        nextLocalDraft,
        resolvedTouchedPaths,
      );

      const savedPlan = serializePlanDraftForSave(mergedDraft);
      if (!savedPlan) {
        throw createFatalSyncError("Cannot save an invalid nutrition plan");
      }

      const plan = new CoachNutritionPlan({
        ...savedPlan,
        id: normalizedPlanId,
        version: Math.max(
          1,
          Number(savedPlan?.version ?? existingPlanRecord?.version ?? 1) || 1,
        ),
      });
      const existingMealsById = new Map(existingMealRecords.map((meal) => [meal.id, meal]));
      const serverDraftMealsById = new Map(
        (Array.isArray(serverDraft?.meals) ? serverDraft.meals : []).map((meal) => [meal.id, meal]),
      );
      nextMealDocs = touchesMeals
        ? plan.toMealDocs().map((mealDoc) => {
            const nextDraftMeal = (Array.isArray(mergedDraft?.meals) ? mergedDraft.meals : []).find(
              (meal) => meal.id === mealDoc.id,
            );
            const previousDraftMeal = serverDraftMealsById.get(mealDoc.id) ?? null;
            if (
              previousDraftMeal &&
              nextDraftMeal &&
              areEqualByJson(previousDraftMeal, nextDraftMeal)
            ) {
              return clonePlainValue(existingMealsById.get(mealDoc.id) ?? mealDoc);
            }

            return mealDoc;
          })
        : existingMealRecords.map((meal) => clonePlainValue(meal));
      const nextMealsById = new Map(nextMealDocs.map((meal) => [meal.id, meal]));
      const nextRev = Number(existingPlanRecord?._rev ?? 0) + 1;
      const shouldClearMergedGoals =
        mergeRootWrite &&
        resolvedTouchedPaths.includes("goals") &&
        savedPlan.goals == null;

      nextPlanDoc = this.buildPlanRootRecord(
        {
          ...savedPlan,
          id: normalizedPlanId,
          version: plan.version,
        },
        nextMealDocs,
        {
          createdAt: existingPlanRecord?.createdAt ?? createdAt ?? serverTimestamp(),
          updatedAt: serverTimestamp(),
          rev: nextRev,
        },
      );

      const rootWriteDoc = shouldClearMergedGoals
        ? {
            ...nextPlanDoc,
            goals: deleteField(),
          }
        : nextPlanDoc;

      if (mergeRootWrite) {
        transaction.set(planDocRef, rootWriteDoc, { merge: true });
      } else {
        transaction.set(planDocRef, rootWriteDoc);
      }

      existingMealsById.forEach((existingMeal, mealId) => {
        if (!nextMealsById.has(mealId)) {
          transaction.delete(
            doc(db, ...buildNutritionPlanMealDocPath(this.coach.id, normalizedPlanId, mealId)),
          );
        }
      });

      nextMealsById.forEach((mealDoc, mealId) => {
        const existingMeal = existingMealsById.get(mealId);
        if (!areEqualByJson(existingMeal, mealDoc)) {
          transaction.set(
            doc(db, ...buildNutritionPlanMealDocPath(this.coach.id, normalizedPlanId, mealId)),
            mealDoc,
          );
        }
      });
    });

    if (!nextPlanDoc) {
      return null;
    }

    runInAction(() => {
      entity?.noteMutationSuccess?.({
        submittedSnapshot: mergedDraft,
        submittedVersion,
        nextRev: nextPlanDoc?._rev,
      });
    });

    const persistedEntity = this.updatePlanCache(
      normalizedPlanId,
      nextPlanDoc,
      nextMealDocs,
      { source: "commit" },
    );
    return {
      entity: persistedEntity,
      planDoc: nextPlanDoc,
      mealDocs: nextMealDocs,
      submittedDraft: mergedDraft,
      _rev: Number(nextPlanDoc?._rev ?? 0) || 0,
    };
  }

  async persistPlan(planData, { baseRev } = {}) {
    const resolvedPlanId = normalizeId(planData?.id, "plan");
    const nextDraft = buildPlanDraftForMerge(planData, planData?.meals ?? []);
    const result = await this.persistResolvedPlanDraft(
      resolvedPlanId,
      () => nextDraft,
      {
        baseRev,
        createdAt: planData?.createdAt ?? null,
        mergeRootWrite: false,
      },
    );
    return result.entity;
  }

  savePlan(planData, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("plan", planData?.id),
      execute: () => this.persistPlan(planData, options),
    });
  }

  async exportClientPlanToLibrary({ clientPlan, clientPlanStore, exportName } = {}) {
    if (!this.coach?.id || !clientPlanStore || !clientPlan?.id) {
      throw createFatalSyncError("Cannot export client nutrition plan without store inputs");
    }

    const normalizedPlanId = normalizeId(clientPlan.id, "plan");
    const sourcePlanEntity =
      clientPlan?.planEntity ??
      clientPlanStore?.planEntitiesById?.get?.(normalizedPlanId) ??
      null;
    const sourcePlanRootRecord =
      clientPlan?.rootRecord ??
      clientPlanStore?.planRootRecordsById?.get?.(normalizedPlanId) ??
      null;
    const { planRecord: sourceDraft } = await resolveNutritionPlanActionSnapshot({
      planId: normalizedPlanId,
      sourceEntity: sourcePlanEntity,
      sourceRootRecord: sourcePlanRootRecord,
      fallbackPlan: clientPlan,
      loadMealRecords: (resolvedPlanId, options = {}) =>
        clientPlanStore.loadPlanMealRecords(resolvedPlanId, options),
    });

    const exportPlanDraft = buildCoachNutritionPlanLibraryExportDraft({
      sourcePlan: sourceDraft,
      exportPlanId: uuidv4(),
      exportName,
    });

    if (!exportPlanDraft) {
      throw createFatalSyncError("Cannot export nutrition plan without a valid name");
    }

    return this.savePlan(exportPlanDraft);
  }

  async movePendingNutritionPlanToLibrary({
    pendingUserId,
    planId,
    fallbackPlanRecord = null,
    language = "en",
    showToast,
  } = {}) {
    if (this.coach?.isOffline === true) {
      return { ok: false, reason: "offline" };
    }

    const pendingWorkspaceSummary =
      this.coach?.findPendingWorkspaceSummaryByPendingUserId?.(pendingUserId) ??
      null;
    if (!pendingWorkspaceSummary || !hasPendingNutritionPlanInSummary(pendingWorkspaceSummary, planId)) {
      return { ok: false, reason: "invalid_pending_context" };
    }

    const moveResult = await movePendingNutritionPlanToLibraryTransactional({
      coachId: this.coach?.id,
      pendingUserId,
      planId,
      fallbackPlanRecord,
      language,
      showToast,
    });

    if (
      moveResult?.ok &&
      moveResult?.movedPlanId &&
      moveResult?.movedPlanData
    ) {
      this.updatePlanCache(
        moveResult.movedPlanId,
        moveResult.movedPlanData,
        Array.isArray(moveResult?.movedMealData) ? moveResult.movedMealData : [],
        { source: "commit" },
      );
    }

    return moveResult;
  }

  async deletePendingNutritionPlan({
    pendingUserId,
    planId,
  } = {}) {
    if (this.coach?.isOffline === true) {
      return { ok: false, reason: "offline" };
    }

    const pendingWorkspaceSummary =
      this.coach?.findPendingWorkspaceSummaryByPendingUserId?.(pendingUserId) ??
      null;
    if (!pendingWorkspaceSummary || !hasPendingNutritionPlanInSummary(pendingWorkspaceSummary, planId)) {
      return { ok: false, reason: "invalid_pending_context" };
    }

    return deletePendingNutritionPlanTransactional({
      coachId: this.coach?.id,
      pendingUserId,
      planId,
    });
  }

  async persistPlanRootPatch(planId, planPatch, { baseRev } = {}) {
    if (!this.coach?.id || !planId) {
      throw createFatalSyncError("Cannot patch nutrition plan root without a plan id");
    }

    const normalizedPlanId = normalizeId(planId, "plan");
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => ({
        ...serverDraft,
        ...planPatch,
      }),
      {
        baseRev,
        requireExistingPlan: true,
      },
    );
    return result.entity;
  }

  patchPlanRoot(planId, planPatch, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("plan", planId),
      execute: () => this.persistPlanRootPatch(planId, planPatch, options),
    });
  }

  async persistPlanMeal(planId, mealData, { baseRev } = {}) {
    if (!this.coach?.id || !planId || !mealData?.id) {
      throw createFatalSyncError("Cannot patch nutrition plan meal without meal data");
    }

    const normalizedPlanId = normalizeId(planId, "plan");
    const normalizedMealId = normalizeId(mealData.id, "meal");
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => {
        const currentMeals = Array.isArray(serverDraft?.meals) ? serverDraft.meals : [];
        const currentMeal = currentMeals.find((meal) => meal.id === normalizedMealId) ?? null;
        const nextMeal = cloneMeal({
          ...(currentMeal ?? {}),
          ...mealData,
          id: normalizedMealId,
          order: mealData?.order ?? currentMeal?.order ?? currentMeals.length,
        });

        return {
          ...serverDraft,
          meals: sortMealsByOrder(
            currentMeals
              .filter((meal) => meal.id !== normalizedMealId)
              .concat(nextMeal),
          ),
        };
      },
      {
        baseRev,
        requireExistingPlan: true,
        touchedPaths: ["meals"],
      },
    );
    return result.entity;
  }

  patchPlanMeal(planId, mealData, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("plan", planId),
      execute: () => this.persistPlanMeal(planId, mealData, options),
    });
  }

  async addPlanMeal(planId, mealData, options = {}) {
    const normalizedMealId = normalizeId(mealData?.id, "meal");
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("plan", planId),
      execute: () =>
        this.persistPlanMeal(
          planId,
          {
            ...mealData,
            id: normalizedMealId,
          },
          options,
        ),
    });
  }

  async persistPlanMealDeletion(planId, mealId, { baseRev } = {}) {
    if (!this.coach?.id || !planId || !mealId) {
      throw createFatalSyncError("Cannot delete nutrition plan meal without ids");
    }

    const normalizedPlanId = normalizeId(planId, "plan");
    const normalizedMealId = normalizeId(mealId, "meal");
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => {
        const currentMeals = Array.isArray(serverDraft?.meals) ? serverDraft.meals : [];
        const existingMeal = currentMeals.find((meal) => meal.id === normalizedMealId);
        if (!existingMeal) {
          return null;
        }

        return {
          ...serverDraft,
          meals: currentMeals.filter((meal) => meal.id !== normalizedMealId),
        };
      },
      {
        baseRev,
        requireExistingPlan: true,
        touchedPaths: ["meals"],
      },
    );
    return result?.entity ?? null;
  }

  deletePlanMeal(planId, mealId, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("plan", planId),
      execute: () => this.persistPlanMealDeletion(planId, mealId, options),
    });
  }

  async persistPlanMealReorder(planId, nextMeals, { baseRev } = {}) {
    if (!this.coach?.id || !planId) {
      throw createFatalSyncError("Cannot reorder nutrition plan meals without a plan id");
    }

    const normalizedPlanId = normalizeId(planId, "plan");
    const normalizedMeals = sortMealsByOrder((Array.isArray(nextMeals) ? nextMeals : []).map(
      (meal, index) => cloneMeal({
        ...clonePlainValue(meal),
        id: normalizeId(meal?.id, `meal-${index}`),
        order: index,
      }),
    ));
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => ({
        ...serverDraft,
        meals: normalizedMeals,
      }),
      {
        baseRev,
        requireExistingPlan: true,
        touchedPaths: ["meals"],
      },
    );
    return result.entity;
  }

  reorderPlanMeals(planId, nextMeals, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("plan", planId),
      execute: () => this.persistPlanMealReorder(planId, nextMeals, options),
    });
  }

  async persistPlanDeletion(planId) {
    if (!this.coach?.id || !planId) {
      return;
    }

    const normalizedPlanId = normalizeId(planId);
    const activePlanMeals = await this.loadPlanMealRecords(normalizedPlanId);
    const batch = writeBatch(db);

    activePlanMeals.forEach((mealDoc) => {
      batch.delete(
        doc(db, ...buildNutritionPlanMealDocPath(this.coach.id, normalizedPlanId, mealDoc.id)),
      );
    });

    batch.delete(doc(db, ...buildNutritionPlanDocPath(this.coach.id, normalizedPlanId)));
    await batch.commit();

    this.syncController.unregisterRead(createPlanReadKey(normalizedPlanId, "root"));
    this.syncController.unregisterRead(createPlanReadKey(normalizedPlanId, "meals"));
    this.removePlanFromStore(normalizedPlanId);
  }

  deletePlan(planId) {
    return this.syncController.runImmediateMutation({
      entityKey: createEntityKey("plan", planId),
      execute: () => this.persistPlanDeletion(planId),
    });
  }
}

export default CoachNutritionStore;
